import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { useAuth } from './AuthContext';
import { useReso } from './ResoContext';
import { useDebugger } from './DebuggerContext';
import { useTenant } from './TenantContext';
import Loader from '../components/common/loader';
import Saas404 from '../components/common/saas/Saas404';

type AuthenticationState =
  | 'initializing'
  | 'checking_ngw'
  | 'checking_reso'
  | 'authenticated'
  | 'error';

interface AuthenticationCoordinatorProps {
  children: ReactNode;
  onStateChange?: (state: AuthenticationState) => void;
}

// Helper function to generate user-friendly loading messages based on auth state
const getLoadingMessage = (state: AuthenticationState): string => {
  switch (state) {
    case 'initializing':
      return 'Initializing authentication...';
    case 'checking_ngw':
      return 'Verifying credentials...';
    case 'checking_reso':
      return 'Establishing secure connection...';
    default:
      return 'Authenticating...';
  }
};

const AuthCoordinator: React.FC<AuthenticationCoordinatorProps> = ({
  children,
  onStateChange,
}) => {
  const {
    isLoading: isTenantConfigLoading,
    getConfigValue,
    isAddEditHost,
    hasProvidedTenantName,
    supportsClientResoWebApi,
  } = useTenant();

  const [willNotLogin, setWillNotLogin] = useState(false);
  const [authState, setAuthState] =
    useState<AuthenticationState>('initializing');
  const [error, setError] = useState<Error | null>(null);
  const hasInitialized = useRef(false);

  const { checkAuthentication } = useAuth();
  const { refreshSession: refreshResoSession } = useReso();
  const debug = useDebugger();

  const updateAuthState = (newState: AuthenticationState) => {
    setAuthState(newState);
    onStateChange?.(newState);
  };

  useEffect(() => {
    if (hasInitialized.current || isTenantConfigLoading) {
      return;
    }

    // Check if we're on addedit host without a tenant name
    // Add a small delay to allow for potential redirects
    if (isAddEditHost() && !hasProvidedTenantName) {
      debug.log(
        'AuthCoordinator',
        'AddEdit host without tenant name - checking for redirect',
        'warn',
      );

      // Wait a bit to see if a redirect happens (from TenantContext)
      // This prevents the 404 flash when redirecting authenticated users
      const timeoutId = setTimeout(() => {
        // If we're still here after the delay, show 404
        if (!hasInitialized.current) {
          debug.log(
            'AuthCoordinator',
            'No redirect occurred - showing 404',
            'warn',
          );
          setWillNotLogin(true);
          hasInitialized.current = true;
        }
      }, 500); // 500ms delay to allow for redirect

      return () => clearTimeout(timeoutId);
    }

    hasInitialized.current = true;

    const coordinateAuthentication = async () => {
      try {
        const resoAuthInProgress = sessionStorage.getItem(
          'reso_auth_in_progress',
        );

        debug.group('AuthCoordinator', 'Starting authentication flow');
        debug.log('AuthCoordinator', 'Checking NGW authentication');
        updateAuthState('checking_ngw');

        const ngwSession = await checkAuthentication();

        if (!ngwSession || !ngwSession.session) {
          if (ngwSession?.willNotLogin) {
            setWillNotLogin(true);
          }

          debug.log(
            'AuthCoordinator',
            'NGW authentication required, initiating redirect',
            'warn',
          );
          return; // checkAuthentication will handle redirect
        }

        debug.log('AuthCoordinator', 'NGW authentication successful');

        if (!supportsClientResoWebApi()) {
          debug.log(
            'AuthCoordinator',
            'Client-side RESO Web API not supported, completing authentication chain.',
          );
          updateAuthState('authenticated');
        } else {
          debug.log(
            'AuthCoordinator',
            'Proceeding to check RESO authentication',
          );
          updateAuthState('checking_reso');

          try {
            const isFinishedRedirecting = await refreshResoSession(
              getConfigValue('RESO_BASE_URL'),
            );

            if (isFinishedRedirecting) {
              sessionStorage.removeItem('reso_auth_in_progress');
              debug.log('AuthCoordinator', 'RESO authentication complete');
              updateAuthState('authenticated');
            } else {
              debug.log(
                'AuthCoordinator',
                'Initiating RESO authentication redirect',
                'warn',
              );
            }
          } catch (resoError) {
            if (resoAuthInProgress) {
              debug.log(
                'AuthCoordinator',
                'RESO authentication failed after redirect attempt',
                'error',
              );
              throw new Error('RESO authentication failed after redirect');
            }
            debug.log(
              'AuthCoordinator',
              'RESO authentication requires redirect',
              'warn',
            );
            return;
          }
        }
      } catch (err) {
        debug.log(
          'AuthCoordinator',
          `Authentication error: ${err instanceof Error ? err.message : 'Unknown error'}`,
          'error',
        );
        setError(
          err instanceof Error ? err : new Error('Authentication failed'),
        );
        updateAuthState('error');
      } finally {
        debug.groupEnd();
      }
    };

    coordinateAuthentication();
  }, [
    isTenantConfigLoading,
    isAddEditHost,
    hasProvidedTenantName,
    checkAuthentication,
    refreshResoSession,
    getConfigValue,
    debug,
  ]);

  if (willNotLogin) {
    // This flow is when the addedit domain does not have enough information to
    // authenticate the user (no tenant in path and not authenticated)
    return <Saas404 target={'AUTH'} />;
  }

  // Render logic with updated loading UI
  if (authState === 'error' && error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded">
        <h2 className="text-red-700 font-semibold">Authentication Error</h2>
        <p className="text-red-600">{error.message}</p>
      </div>
    );
  }

  if (authState !== 'authenticated') {
    // Show loading indicator in a central location, but header/footer
    // may still be rendered based on branding context
    return (
      <Loader showHeaderFooter={false} text={getLoadingMessage(authState)} />
    );
  }

  // Authentication complete - render the actual app content
  return <>{children}</>;
};

export default AuthCoordinator;
